import React, { useRef, useState } from 'react';
import { sendVideoConsultationEmail } from '../../api-client';
import { useNavigate } from 'react-router-dom';

interface VideoConsultationProps {
  closeModal: () => void;
}

const BookVideoConsultation: React.FC<VideoConsultationProps> = ({ closeModal }) => {
  const navigate = useNavigate();
  const dateInputRef = useRef<HTMLInputElement>(null);
  const timeInputRef = useRef<HTMLInputElement>(null);

  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phoneNumber: '',
    date: '',
    message: '',
    consultationTime: '',
  });

  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [loading, setLoading] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prevFormData => ({
        ...prevFormData,
        [name]: value,
    }));
  };

  const handleClick = (ref: React.RefObject<HTMLInputElement>) => {
    ref.current?.focus();
  };

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};

    if (!formData.fullName.trim()) newErrors.fullName = 'Full Name is required';
    if (!formData.email.trim()) newErrors.email = 'E-Mail is required';
    if (!/^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/.test(formData.email)) newErrors.email = 'Invalid E-Mail format';
    if (!formData.phoneNumber.trim()) newErrors.phoneNumber = 'Phone Number is required';
    if (!formData.date.trim()) newErrors.date = 'Date is required';
    if (!formData.consultationTime.trim()) newErrors.consultationTime = 'Consultation Time is required';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      setLoading(true);
      try {
        await sendVideoConsultationEmail(formData);
        alert('Video consultation booked successfully!');
        navigate('/thank-you');
      } catch (error) {
        alert('Failed to book video consultation');
        console.error('Error booking video consultation:', error);
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-black opacity-50"></div>
      <div className="relative p-6 bg-white rounded-lg shadow-lg w-full max-w-md">
        <button onClick={closeModal} className="absolute top-3 right-3 text-gray-600 hover:text-gray-800">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
        <h2 className="text-xl font-bold text-center mb-4">Book Your Video Consultation</h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="fullName" className="block text-sm font-medium text-gray-700">Full Name</label>
            <input type="text" name="fullName" id="fullName"
              className={`mt-1 block w-full px-3 py-2 border rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-[#408BFF] focus:border-[#408BFF] sm:text-sm ${errors.fullName ? "border-red-500" : "border-gray-300"}`}
              value={formData.fullName} onChange={handleChange} />
            {errors.fullName && <p className="mt-1 text-red-500 text-xs italic">{errors.fullName}</p>}
          </div>
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700">e-Mail</label>
            <input type="email" name="email" id="email"
              className={`mt-1 block w-full px-3 py-2 border rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-[#408BFF] focus:border-[#408BFF] sm:text-sm ${errors.email ? "border-red-500" : "border-gray-300"}`}
              value={formData.email} onChange={handleChange} />
            {errors.email && <p className="mt-1 text-red-500 text-xs italic">{errors.email}</p>}
          </div>
          <div>
            <label htmlFor="phoneNumber" className="block text-sm font-medium text-gray-700">Contact Number</label>
            <input type="text" name="phoneNumber" id="phoneNumber"
              className={`mt-1 block w-full px-3 py-2 border rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-[#408BFF] focus:border-[#408BFF] sm:text-sm ${errors.phoneNumber ? "border-red-500" : "border-gray-300"}`}
              value={formData.phoneNumber} onChange={handleChange} />
            {errors.phoneNumber && <p className="mt-1 text-red-500 text-xs italic">{errors.phoneNumber}</p>}
          </div>
          <div>
            <label htmlFor="date" className="block text-sm font-medium text-gray-700">Pick a Date</label>
            <input type="date" name="date" id="date"
              ref={dateInputRef}
              onClick={() => handleClick(dateInputRef)}
              className={`mt-1 block w-full px-3 py-2 border rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-[#408BFF] focus:border-[#408BFF] sm:text-sm ${errors.date ? "border-red-500" : "border-gray-300"}`}
              value={formData.date} onChange={handleChange} />
            {errors.date && <p className="mt-1 text-red-500 text-xs italic">{errors.date}</p>}
          </div>
          <div>
            <label htmlFor="consultationTime" className="block text-sm font-medium text-gray-700">Preferred Time</label>
            <input type="time" name="consultationTime" id="consultationTime"
              ref={timeInputRef}
              onClick={() => handleClick(timeInputRef)}
              className={`mt-1 block w-full px-3 py-2 border rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-[#408BFF] focus:border-[#408BFF] sm:text-sm`}
              value={formData.consultationTime} onChange={handleChange} />
              {errors.consultationTime && <p className="mt-1 text-red-500 text-xs italic">{errors.consultationTime}</p>}
          </div>
          <div>
            <label htmlFor="message" className="block text-sm font-medium text-gray-700">Message</label>
            <textarea id="message" name="message"
              className="mt-1 block w-full px-3 py-2 border rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-[#408BFF] focus:border-[#408BFF] sm:text-sm"
              value={formData.message} onChange={handleChange} />
          </div>
          <button type="submit" disabled={loading}
            className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#0050b8] hover:bg-[#00285c] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#408BFF]">
            {loading ? 'Booking...' : 'Book Consultation'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default BookVideoConsultation;
